/**
 * CorrectionHistoryTable - clean table view of AI classification corrections.
 *
 * Replaces the prior card-list layout that displayed `old/new_classification`
 * raw - which produced an inconsistent UX where some rows showed a GL name
 * ("Salaries & Wages") and others showed the raw GL code ("1000", "6700").
 *
 * Resolution rules (`resolveGlLabel`):
 *   • If value is purely numeric → treat as GL code; resolve `name` from COA.
 *   • If value is "1000 - Cash" → split.
 *   • Otherwise → assume `name`; try reverse-lookup to find code.
 *   • Underscores → spaces for classification slugs (e.g. `loan_received`).
 */
import React from 'react';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { BookOpen, ChevronRight } from 'lucide-react';

const isNumericCode = (v) => /^\d{3,5}$/.test(String(v || '').trim());

export const resolveGlLabel = (raw, glAccounts = []) => {
  if (!raw) return { name: 'Unknown', code: null };
  const s = String(raw).trim();
  // Pattern: "1000 - Cash" or "1000 - Cash"
  const dashMatch = s.match(/^(\d{3,5})\s*[--–]\s*(.+)$/);
  if (dashMatch) {
    return { code: dashMatch[1], name: dashMatch[2].trim() };
  }
  if (isNumericCode(s)) {
    const acc = glAccounts.find(a => String(a.code) === s || String(a.gl_code) === s);
    return { code: s, name: acc?.name || acc?.gl_name || 'GL ' + s };
  }
  // Treat as name (or classification slug like "loan_received")
  const pretty = s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const acc = glAccounts.find(a =>
    (a.name || '').toLowerCase() === pretty.toLowerCase()
    || (a.gl_name || '').toLowerCase() === pretty.toLowerCase()
  );
  return { code: acc?.code || acc?.gl_code || null, name: pretty };
};

const Chip = ({ tone, label, code, testId }) => {
  const tones = {
    red: 'bg-rose-50 border-rose-200 text-rose-700',
    green: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 border rounded-md text-xs font-medium ${tones[tone] || ''}`}
      data-testid={testId}
    >
      <span>{label}</span>
      {code && <span className="text-[10px] font-mono opacity-60">· {code}</span>}
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

const CorrectionHistoryTable = ({ correctionHistory = [], glAccounts = [], formatAmount }) => {
  if (!correctionHistory || correctionHistory.length === 0) {
    return (
      <Card data-testid="correction-history-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#108981]" />
            Correction History
          </CardTitle>
          <CardDescription>Your past corrections that help the AI learn</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 border-2 border-dashed rounded-lg">
            <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No corrections recorded yet</p>
            <p className="text-sm text-muted-foreground mt-1">Reclassify transactions to help the AI learn</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="correction-history-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#108981]" />
          Correction History
          <Badge variant="outline" className="ml-2 text-[10px]">{correctionHistory.length}</Badge>
        </CardTitle>
        <CardDescription>Your past corrections that help the AI learn</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="border rounded-lg overflow-hidden">
          {/* Header row - desktop only (cards take over on mobile). */}
          <div
            className="hidden md:grid items-center text-[11px] uppercase tracking-wider text-muted-foreground font-medium px-3 py-2 border-b bg-muted/30"
            style={{ gridTemplateColumns: '110px 1.6fr 1.2fr 1.6fr 110px' }}
          >
            <div>Date</div>
            <div>Description</div>
            <div>Counterparty</div>
            <div>Correction</div>
            <div className="text-right">Amount</div>
          </div>

          <ScrollArea className="max-h-[420px]">
            {correctionHistory.map((correction, index) => {
              const oldLabel = resolveGlLabel(correction.old_classification, glAccounts);
              const newLabel = resolveGlLabel(correction.new_classification, glAccounts);
              return (
                <div
                  key={correction.id || index}
                  className="border-b last:border-b-0 hover:bg-muted/10 transition-colors"
                  data-testid={`correction-row-${index}`}
                >
                  {/* Desktop grid */}
                  <div
                    className="hidden md:grid items-center gap-2 px-3 py-3 text-sm"
                    style={{ gridTemplateColumns: '110px 1.6fr 1.2fr 1.6fr 110px' }}
                  >
                    <div className="text-xs text-muted-foreground">{formatDate(correction.corrected_at)}</div>
                    <div className="min-w-0">
                      <p className="truncate font-medium" title={correction.transaction_description}>
                        {correction.transaction_description || 'Transaction'}
                      </p>
                      {correction.reason && (
                        <p className="text-[11px] text-muted-foreground italic truncate" title={correction.reason}>
                          &ldquo;{correction.reason}&rdquo;
                        </p>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate" title={correction.transaction_counterparty}>
                      {correction.transaction_counterparty || '-'}
                    </div>
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Chip tone="red" label={oldLabel.name} code={oldLabel.code} testId={`correction-from-${index}`} />
                      <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
                      <Chip tone="green" label={newLabel.name} code={newLabel.code} testId={`correction-to-${index}`} />
                    </div>
                    <div className="text-right">
                      <p className="font-semibold tabular-nums">
                        {formatAmount ? formatAmount(correction.transaction_amount) : correction.transaction_amount}
                      </p>
                      {correction.similar_transactions_count > 0 && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {correction.similar_transactions_count} similar
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Mobile card layout - amount, badges and counterparty stack
                      cleanly so nothing overlaps on a ~375px screen. */}
                  <div className="md:hidden p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate" title={correction.transaction_description}>
                          {correction.transaction_description || 'Transaction'}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          {formatDate(correction.corrected_at)}
                          {correction.transaction_counterparty && (
                            <> · {correction.transaction_counterparty}</>
                          )}
                        </p>
                      </div>
                      <p className="font-semibold tabular-nums text-sm whitespace-nowrap">
                        {formatAmount ? formatAmount(correction.transaction_amount) : correction.transaction_amount}
                      </p>
                    </div>
                    <div className="flex items-center flex-wrap gap-1.5">
                      <Chip tone="red" label={oldLabel.name} code={oldLabel.code} testId={`correction-from-${index}`} />
                      <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
                      <Chip tone="green" label={newLabel.name} code={newLabel.code} testId={`correction-to-${index}`} />
                    </div>
                    {correction.reason && (
                      <p className="text-[11px] text-muted-foreground italic truncate" title={correction.reason}>
                        &ldquo;{correction.reason}&rdquo;
                      </p>
                    )}
                    {correction.similar_transactions_count > 0 && (
                      <p className="text-[10px] text-muted-foreground">
                        {correction.similar_transactions_count} similar transaction{correction.similar_transactions_count > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </ScrollArea>
        </div>
      </CardContent>
    </Card>
  );
};

export default CorrectionHistoryTable;
export { CorrectionHistoryTable };
